import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Field, TextArea } from './fields';
import type { DraftStage } from './types';

export interface StagesFieldProps {
  stages: DraftStage[];
  soft: boolean;
  onChange: (next: DraftStage[]) => void;
}

/** Stages with their goal, guidance and — in guided mode — their conditions. */
export function StagesField({ stages, soft, onChange }: StagesFieldProps) {
  const patch = (i: number, part: Partial<DraftStage>) =>
    onChange(stages.map((s, j) => (j === i ? { ...s, ...part } : s)));

  return (
    <fieldset className="space-y-3">
      <legend className="text-xs font-medium text-muted-fg">
        Этапы {soft ? '(цели разговора — агент сам решает, когда двигаться)' : '(жёсткий граф переходов)'}
      </legend>
      {stages.map((stage, i) => (
        <div key={i} className="space-y-2 rounded border border-border p-2">
          <div className="flex gap-2">
            <Field label={`Идентификатор этапа ${i + 1}`}>
              <Input
                aria-label={`Идентификатор этапа ${i + 1}`}
                value={stage.id ?? ''}
                onChange={(e) => patch(i, { id: e.target.value })}
              />
            </Field>
            <Field label={`Цель этапа ${i + 1}`}>
              <Input
                aria-label={`Цель этапа ${i + 1}`}
                placeholder="Понять задачу и бюджет"
                value={stage.goal ?? ''}
                onChange={(e) => patch(i, { goal: e.target.value })}
              />
            </Field>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Удалить этап ${i + 1}`}
              onClick={() => onChange(stages.filter((_, j) => j !== i))}
            >
              ✕
            </Button>
          </div>
          <Field label={`Пояснение для агента (этап ${i + 1})`} hint="Служебный текст: клиенту он не зачитывается.">
            <TextArea
              aria-label={`Пояснение для агента (этап ${i + 1})`}
              rows={2}
              value={stage.instruction ?? ''}
              onChange={(e) => patch(i, { instruction: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-xs text-muted-fg">
            <input
              type="checkbox"
              checked={stage.must_include_opt_out === true}
              onChange={(e) => patch(i, { must_include_opt_out: e.target.checked })}
            />
            Обязательно упомянуть отписку
          </label>
          <TransitionsField
            index={i}
            soft={soft}
            transitions={stage.transitions ?? []}
            onChange={(transitions) => patch(i, { transitions })}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onChange([...stages, { id: `stage_${stages.length + 1}`, instruction: '', goal: '', transitions: [] }])}
      >
        + Этап
      </Button>
    </fieldset>
  );
}

function TransitionsField({
  index,
  soft,
  transitions,
  onChange,
}: {
  index: number;
  soft: boolean;
  transitions: { to: string; when: string }[];
  onChange: (next: { to: string; when: string }[]) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-fg">
        Условия перехода{soft ? ' (необязательны в мягком режиме)' : ''}
      </div>
      {transitions.map((t, j) => (
        <div key={j} className="flex gap-1">
          <Input
            aria-label={`Этап ${index + 1}: условие ${j + 1}`}
            placeholder="когда клиент ответил"
            value={t.when}
            onChange={(e) => onChange(transitions.map((x, k) => (k === j ? { ...x, when: e.target.value } : x)))}
          />
          <Input
            aria-label={`Этап ${index + 1}: переход ${j + 1}`}
            placeholder="qualify или @goal"
            value={t.to}
            onChange={(e) => onChange(transitions.map((x, k) => (k === j ? { ...x, to: e.target.value } : x)))}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Удалить переход ${j + 1} этапа ${index + 1}`}
            onClick={() => onChange(transitions.filter((_, k) => k !== j))}
          >
            ✕
          </Button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...transitions, { when: '', to: '' }])}>
        + Переход
      </Button>
    </div>
  );
}
